/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { connect } from 'react-redux'
import { getSelfUser } from '@sportninja/common/selectors/users'
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react'
import { t } from '@sportninja/common/i18n'
import { media, Mobile } from '../../components/Responsive'
import UserSidebar from './UserSidebar'
import Helmet from '../../components/Helmet'
import { HomePaymentBanner } from '../RegistrationPayment/HomePaymentBanner'
import { HomeBannerParticipantsLimit } from '../../components/HomeBannerParticipantsLimit'
import PostInput from 'src/components/PostInput'
import HighlightWidget from './components/HighlightWidget'
import FeedPage from 'src/components/FeedPage'
import FeedPostCreateModal from 'src/components/FeedPostCreateModal'
import OnboardingWizardModal from '../../components/ts-components/OnboardingWizardModal'
import ContinueTutorialBanner from '../../components/ContinueTutorialBanner'
import { useOnboardingTutorial } from '../../hooks/useOnboardingTutorial'
import { useSelector, useDispatch } from 'src/hooks/useRedux'
import accountActions from '@sportninja/common/actions/account'
import { isCanlan } from '@sportninja/common/utils/customer-name'

const AccountType = {
  FREE: 1,
  PROFESSIONAL: 2,
  ULTIMATE: 3,
}

const Home = ({ user = {}, me = {}, isLoggedIn }) => {
  const dispatch = useDispatch()
  const refreshNewFeed = useRef(() => {})
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [postToEdit, setPostToEdit] = useState(null)
  const [isLoadingFeed, setIsLoadingFeed] = useState(false)
  const [sidebarEmpty, setSidebarEmpty] = useState(false)
  const account = useSelector((state) => state.account._meAccount)

  useLayoutEffect(() => {
    if (me?.account_id) {
      dispatch(accountActions.fetchAccount.request(null, null, me.account_id))
    }
  }, [me?.account_id])

  // Use the onboarding tutorial hook
  const {
    shouldShowModal,
    shouldShowBanner,
    updateTutorialStatus,
    openModal: openOnboardingModal,
    closeModal: closeOnboardingModal,
  } = useOnboardingTutorial({ me, isLoggedIn })

  const handleOnboardingSkip = useCallback(async () => {
    await updateTutorialStatus('skipped')
  }, [updateTutorialStatus])

  const handleOnboardingComplete = useCallback(async () => {
    await updateTutorialStatus('completed')
  }, [updateTutorialStatus])

  useEffect(() => {
    const feedPage = document.getElementById('feed-page')
    if (isModalOpen) {
      feedPage.style.display = 'none'
    } else {
      feedPage.style.display = 'block'
    }
  }, [isModalOpen])

  const isBusinessAccount = useMemo(() => {
    return me?.account_id && me?.account_type && me?.account_type > 1
  }, [me])

  const shouldDisplayUpgradeBanner = useMemo(() => {
    return (
      Number(account?.subscription_type?.id) === AccountType.FREE &&
      isBusinessAccount &&
      !isCanlan
    )
  }, [account?.subscription_type?.id, isBusinessAccount])

  useEffect(() => {
    localStorage.removeItem('isLightMode')
  }, [])

  return (
    <div
      css={css`
        display: flex;
        flex: 1;
        width: 100%;
        margin-top: 16px;
      `}
    >
      <OnboardingWizardModal
        isOpen={shouldShowModal}
        onClose={closeOnboardingModal}
        onSkip={handleOnboardingSkip}
        onComplete={handleOnboardingComplete}
      />
      <Helmet
        title={t('util:capitalize', {
          text: t('common:home'),
        })}
      />
      <Mobile>
        {(isMobile) => {
          return (
            <>
              <FeedPostCreateModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                refreshNewFeed={refreshNewFeed.current}
                postToEdit={postToEdit}
                setPostToEdit={setPostToEdit}
              />

              <div
                css={css`
                  display: flex;
                  justify-content: center;
                  width: 100%;
                `}
              >
                <div
                  css={css`
                    margin-right: 8px;
                    width: 100%;
                    max-width: 780px;
                    ${sidebarEmpty
                      ? 'margin-right: 55px;'
                      : 'margin-right: 8px;'}
                    ${media.mobile} {
                      margin-left: 0 !important;
                      margin-right: 0 !important;
                      width: calc(100% - 32px);
                    }
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      gap: 16px;
                    `}
                  >
                    <HomeBannerParticipantsLimit />
                    {isMobile && shouldShowBanner ? (
                      <div
                        css={css`
                          margin-bottom: -8px;
                        `}
                      >
                        <ContinueTutorialBanner
                          onContinue={openOnboardingModal}
                        />
                      </div>
                    ) : null}
                    <HomePaymentBanner />
                    {!isLoadingFeed ? (
                      <PostInput
                        onInputClick={() => setIsModalOpen(true)}
                        avatarUrl={user?.imageId}
                      />
                    ) : null}
                    {!isLoadingFeed ? <HighlightWidget /> : null}
                    <FeedPage
                      avatarUrl={user?.imageId}
                      refreshNewFeed={refreshNewFeed}
                      user={user}
                      setPostToEdit={setPostToEdit}
                      setModalOpen={setIsModalOpen}
                      setIsLoadingFeed={setIsLoadingFeed}
                      setIsModalOpen={setIsModalOpen}
                      isModalOpen={isModalOpen}
                    />
                  </div>
                </div>
                {!isMobile ? (
                  <UserSidebar
                    shouldShowOnboardingBanner={shouldShowBanner}
                    onContinueOnboarding={openOnboardingModal}
                    setSidebarEmpty={setSidebarEmpty}
                    shouldDisplayUpgradeBanner={shouldDisplayUpgradeBanner}
                  />
                ) : null}
              </div>
            </>
          )
        }}
      </Mobile>
    </div>
  )
}

const mapStateToProps = (state) => {
  return {
    user: getSelfUser(state),
    me: getSelfUser(state),
    isLoggedIn: state.auth.login.success,
  }
}

export default connect(mapStateToProps)(Home)
